import { create } from 'zustand';
import { logger } from '@/services/logger';

/**
 * Type d'opération de sauvegarde
 * Extended pour supporter tous les types de synchronisation offline-first
 */
export type SaveOperationType = 
  | 'goals' 
  | 'progress' 
  | 'wallet' 
  | 'validation' 
  | 'generic'
  | 'offline-queue'  // Ajout à la queue offline
  | 'push'           // Envoi vers serveur
  | 'pull'           // Récupération serveur
  | 'conflict';      // Résolution de conflit

/**
 * Opération de sauvegarde en cours
 */
export interface SaveOperation {
  id: string;
  type: SaveOperationType;
  startedAt: Date;
}

/**
 * État du store d'indicateur de sauvegarde
 */
interface SaveIndicatorState {
  operations: SaveOperation[];
  lastSaveAt: Date | null;
  lastError: string | null;
  wasRecentlySaved: boolean; // État explicite pour l'animation de succès
  
  // Actions
  startSave: (type: SaveOperationType) => string;
  endSave: (operationId: string, error?: string) => void;
  clearError: () => void;
}

/**
 * Store Zustand pour tracker les opérations de sauvegarde vers Supabase
 * Utilisé pour afficher un indicateur visuel en temps réel
 */
export const useSaveIndicatorStore = create<SaveIndicatorState>((set, get) => ({
  operations: [],
  lastSaveAt: null,
  lastError: null,
  wasRecentlySaved: false,

  startSave: (type: SaveOperationType) => {
    const operationId = `${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    logger.debug('[SaveIndicator] Starting save operation', { 
      operationId, 
      type,
      currentOperations: get().operations.length 
    });

    set(state => ({
      operations: [
        ...state.operations,
        {
          id: operationId,
          type,
          startedAt: new Date()
        }
      ],
      lastError: null // Effacer l'erreur précédente
    }));

    return operationId;
  },

  endSave: (operationId: string, error?: string) => {
    const operation = get().operations.find(op => op.id === operationId);
    
    if (!operation) {
      logger.warn('[SaveIndicator] Operation not found', { operationId });
      return;
    }

    const duration = Date.now() - operation.startedAt.getTime();

    if (error) {
      logger.error('[SaveIndicator] Save operation failed', { 
        operationId, 
        type: operation.type,
        duration,
        error 
      });

      set(state => ({
        operations: state.operations.filter(op => op.id !== operationId),
        lastError: error
      }));
    } else {
      logger.debug('[SaveIndicator] Save operation completed', { 
        operationId, 
        type: operation.type,
        duration
      });

      set(state => ({
        operations: state.operations.filter(op => op.id !== operationId),
        lastSaveAt: new Date(),
        lastError: null,
        wasRecentlySaved: true // Activer le flag de succès récent
      }));

      // Auto-reset du flag après 2 secondes
      setTimeout(() => {
        set({ wasRecentlySaved: false });
      }, 2000);
    }
  },

  clearError: () => {
    set({ lastError: null });
  }
}));

/**
 * Accesseurs computed pour l'état de sauvegarde
 */
export const saveIndicatorSelectors = {
  isSaving: (state: SaveIndicatorState) => state.operations.length > 0,
  operationCount: (state: SaveIndicatorState) => state.operations.length,
  currentOperationType: (state: SaveIndicatorState) => 
    state.operations.length > 0 ? state.operations[0].type : null,
  hasError: (state: SaveIndicatorState) => state.lastError !== null
};
